import { Component, Input, EventEmitter, Output, ViewChildren, ViewContainerRef, QueryList, TemplateRef } from '@angular/core';

import { DynamicComponent, GenericValue, HisBaseGlobalsService, HisControlValueAccessor,
    HisDdDirectiveRegistryService } from 'hisng';

import { DYNAMIC_FIELD_ANCHOR } from 'app/fs/res/common/model/dynamic-content/constants';
import { ProviderManager } from 'app/fs/res/common/utils/provider-manager';
import { ResearchPartnerSimple, ResearchPartner } from 'app/fs/res/common/model/researchPartner/researchPartner';

@Component( {
    selector: 'res-organizations-panel',
    templateUrl: './organizations-panel.component.html',
    providers: [ ProviderManager.getComponentProvider(OrganizationsPanelComponent),
        ProviderManager.getValueAccessorProvider(OrganizationsPanelComponent) ],
} )
export class OrganizationsPanelComponent extends HisControlValueAccessor {
    internalOrganizations: GenericValue[]; // ngModel
    externalOrganizations: ResearchPartnerSimple[]; // ngModel

    @Input('ngModel') set _organizations( organizations: any ) {
        // #309596 internal/external organizations müssen auch auf null gesetzt werden wenn von aussen null oder undefined reinkommt
        console.log('OrganizationsPanelComponent set organizations:', organizations);
        this.internalOrganizations = organizations?.internalOrganizations ? organizations.internalOrganizations : null;
        this.externalOrganizations = organizations?.externalOrganizations ? organizations.externalOrganizations : null;
    }
    
    /** The data dictionary key */
    ddKey: string;
    /** input elements are disabled if true */
    readonly = false;

    @Input('ddKey') set _ddKey( ddKey: string ) {
        this.ddKey = ddKey;
        this.readonly = this.hisBaseGlobalsService.getDd(this.ddKey)?.readonly;
    }

    @Input() newResearchPartner: ResearchPartner;  // new instance for researchPartner
    @Input() editMode = false;
    @Input() singleSelect = false;
    @Input() header: string;
    @Input() noAssignedMessage: string;
    @Input() infoMessage: string;
    @Input() assignLink: string;
    @Input() showCreateLink = false;
    @Input() isAddOrganization = false;
    @Input() clearAfterSelection = false;
    @Input() isTreeSelectInline = false;
    @Input() isCreateInline = false;

    @Input() tableTemplate: TemplateRef<any>;

    @Input() dynamicComponent: DynamicComponent;
    @ViewChildren( DYNAMIC_FIELD_ANCHOR, { read: ViewContainerRef } ) fieldViewContainerList: QueryList<ViewContainerRef>;

    public showCreateExternalOrganizationDialog = false;

    @Output() internalOrganizationsChange: EventEmitter<GenericValue[]> = new EventEmitter();
    @Output() deleteInternalOrganizationChange: EventEmitter<GenericValue> = new EventEmitter();
    @Output() externalOrganizationsChange: EventEmitter<ResearchPartnerSimple[]> = new EventEmitter();
    @Output() deleteExternalOrganizationChange: EventEmitter<ResearchPartnerSimple> = new EventEmitter();
    @Output() createExternalOrgUnitChange: EventEmitter<void> = new EventEmitter();

    constructor( public hisBaseGlobalsService: HisBaseGlobalsService,
        public hisDdDirectiveRegistryService: HisDdDirectiveRegistryService ) {
        super();
    }

    /**
     * implementation of event handler for internal organizations
     *
     * @param event GenericValue[] value to change
     */
    selectedInternalOrgUnitChange( event: GenericValue[] ): void {
        this.setIsAddOrganization( false );
        this.internalOrganizationsChange.emit( event );
        this.onModelChange(this);
    }

    /* call back implementation of deleteInternalOrgUnitChange */
    deleteInternalOrgUnitChange( event: GenericValue ): void {
        this.deleteInternalOrganizationChange.emit( event );
    }

    /**
     * implementation of event handler for selected external organizations
     *
     * @param event ResearchPartnerSimple[] selected value to change
     */
    selectedExternalOrgUnitChange( event: ResearchPartnerSimple[] ): void {
        this.setIsAddOrganization( false );
        this.externalOrganizationsChange.emit( event );
        this.onModelChange(this);
    }

    /* call back implementation of deleteExternalOrgUnitChange */
    deleteExternalOrgUnitChange( event: ResearchPartnerSimple ): void {
        this.deleteExternalOrganizationChange.emit( event );
    }

    /* handle call back to create new researchPartner (external organization unit), show create dialog */
    createExternalOrgUnit() {
        this.showCreateExternalOrganizationDialog = true;
    }

    /**
     * method to set visibility of select-box and assign-button
     *
     * @param isAddOrganization boolean to set visibility
     */
    setIsAddOrganization( isAddOrganization: boolean ): void {
        if ( this.singleSelect &&
            ( ( this.internalOrganizations && this.internalOrganizations.length > 0 ) ||
                ( this.externalOrganizations && this.externalOrganizations.length > 0 ) ) ) {
            this.isAddOrganization = false;
        } else {
            this.isAddOrganization = isAddOrganization;
        }
    }

    /* set edit mode and show assign button */
    showAssignButton() {
        if ( this.singleSelect &&
            ( ( this.internalOrganizations && this.internalOrganizations.length > 0 ) ||
                ( this.externalOrganizations && this.externalOrganizations.length > 0 ) ) ) {
            return false;
        } else {
            return this.editMode && !this.isAddOrganization;
        }
    }

    /* create external organization dialog call back function */
    public onFinishCreateExternalOrganizationDialog( event: ResearchPartnerSimple ) {
        this.externalOrganizations = [...this.externalOrganizations, event];
        this.setIsAddOrganization( false );
        this.showCreateExternalOrganizationDialog = false;
        this.externalOrganizationsChange.emit( this.externalOrganizations );
        this.onModelChange(this);
    }
}
